import { makeApiRequest } from '@/src/lib/api-request'
import { useToken } from '@/src/store/token-store'
import { useUser } from '@/src/store/user-store'
import { useInfiniteQuery } from '@tanstack/react-query'
import { RedemptionStatus } from './redemption'
import { formatDate } from '@/src/lib/utils'

interface ContentBreakdown {
  UniqueWatchTime: string
  watch_time: number
  duration: number
  status: string
  watchCount: number
  earning: string
  lykCoin: string
  eventId: string
  eventTitle: string
  eventContent: string
  thumbnailImage: string
  earningtype: 'all' | 'verify'
  channelName: string
  channelShareName: string
  encryptVideoUrl: string
  type:
    | 'viewer'
    | 'creator'
    | 'bonus'
    | 'premium'
    | 'oldEarnings'
    | 'postive_earning'
    | 'others_earning'
  title: string
  description: string
  createdAt: string
  oldEarnings: string
  rewardType: '0' | '1' | '2' | '3' | '4'
}

interface RedemptionBreakdown {
  amount: string
  status: RedemptionStatus
  rewardCoins: string
  createdAt: string
  type: 'redeem'
  [key: string]: any
}

interface AllBreakdownResponse {
  allBreakdown: (ContentBreakdown | RedemptionBreakdown)[]
  offset: number
}

export const useAllBreakdown = ({
  startDate,
  endDate,
  limit,
  offset,
  filterData,
  earningsType,
  sreach_text,
  sortBy,
}: {
  startDate?: Date
  endDate?: Date
  limit: number
  offset: number
  filterData?: Array<string>
  earningsType?: string
  sreach_text?: string
  sortBy: string
}) => {
  const { _hasHydrated } = useUser()
  const { accessToken } = useToken()

  return useInfiniteQuery({
    initialPageParam: { offset: 0 },
    queryKey: [
      'breakdown',
      'all',
      limit,
      offset,
      filterData,
      earningsType,
      sreach_text,
      sortBy,
      startDate,
      endDate,
    ],
    queryFn: async (): Promise<
      AllBreakdownResponse & { pageCount: number }
    > => {
      const newOffset = offset || 0

      const dataProps =
        startDate && endDate
          ? {
              limit: limit,
              startDate: formatDate(startDate),
              endDate: formatDate(endDate),
              offset: newOffset,
              earningType: earningsType,
              filterData: filterData?.length === 0 ? ['all'] : filterData,
              sreach_text: sreach_text,
              sortBy: sortBy,
            }
          : {
              limit: limit,
              offset: newOffset,
              earningType: earningsType,
              filterData: filterData?.length === 0 ? ['all'] : filterData,
              sreach_text: sreach_text,
              sortBy: sortBy,
            }

      const response = await makeApiRequest({
        method: 'POST',
        url: '/Earning/getAllEarningDetails',
        isAuthenticated: true,
        data: dataProps,
        fallbackError: 'Error while fetching all breakdown',
        isV3Api: true,
      })
      const allBreakdown: (ContentBreakdown | RedemptionBreakdown)[] =
        response.response ? response.response : []
      const pageCount: number = response.pageCount || 1

      return { allBreakdown, offset: newOffset, pageCount }
    },
    getNextPageParam: (lastPage) => {
      const newOffset = lastPage.offset + limit
      return lastPage.allBreakdown.length === limit
        ? { offset: newOffset }
        : undefined
    },

    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    enabled: !!accessToken && _hasHydrated,
  })
}
